<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import RedSpot from '@/components/RedSpot.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  hasTitle,
  image,
  hasImage,
  features,
  hasFeatures,
  services,
  hasServices,
  redSpot,
  hasRedSpot,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
      backgroundColor === 'black' ? 'white' : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        :class="[
          'ukravto-service',
          backgroundColor ? `ukravto-service--${backgroundColor}` : '',
        ]"
      >
        <RedSpot
          v-if="hasRedSpot"
          :position-vertical="redSpot.vertical"
          :position-horizontal="redSpot.horizontal"
        />
        <div class="ukravto-service__features">
          <div class="ukravto-service__features-header">
            <NuxtImg
              v-if="hasImage"
              :src="image?.src"
              :alt="image?.alt"
              :height="image?.height"
              :width="image?.width"
              class="ukravto-service__features-image"
              data-aos="fade-in"
              data-aos-delay="100"
              loading="lazy"
            />
            <h2
              v-if="hasTitle"
              class="mb-0 ukravto-service__features-title h2"
              data-aos="fade-in"
              data-aos-delay="100"
              v-html="title"
            ></h2>
          </div>
          <div v-if="hasFeatures" class="ukravto-service__features-items">
            <div
              v-for="(feature, featureIndex) in features"
              :key="feature.title"
              class="ukravto-service__feature"
              data-aos="fade-up"
              :data-aos-delay="
                featureIndex >= 1 ? 100 + 50 * featureIndex : 100
              "
            >
              <p
                v-if="feature.title && feature.title.length > 0"
                class="mb-0 ukravto-service__feature-title h4"
                v-html="feature.title"
              ></p>
              <p
                v-if="feature.description && feature.description.length > 0"
                class="mb-0 ukravto-service__feature-description body-3-regular"
                v-html="feature.description"
              ></p>
            </div>
          </div>
        </div>
        <ul v-if="hasServices" class="m-0 p-0 ukravto-service__services">
          <li
            v-for="(service, serviceIndex) in services"
            :key="service.title"
            data-aos="fade-up"
            :data-aos-delay="serviceIndex >= 1 ? 100 + 50 * serviceIndex : 100"
            class="m-0 ukravto-service__service"
          >
            <p
              v-if="service.title && service.title.length > 0"
              class="ukravto-service__service-title h6"
              v-html="service.title"
            ></p>
            <Button v-slot="slotProps" as-child rounded variant="outlined">
              <CTagDetect
                :to="service.url"
                :class="slotProps.class"
                class="ukravto-service__service-button button-arrow js-button-ukravto-service"
                :aria-label="service.title"
              >
                <CIcon
                  :name="
                    backgroundColor === 'black'
                      ? 'direction-white'
                      : 'direction-black'
                  "
                  width="16"
                  height="16"
                  class="button-arrow__icon"
                />
              </CTagDetect>
            </Button>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.ukravto-service {
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  overflow: hidden;
  position: relative;
  &--black {
    background-color: $surface-container;
  }
  &--white {
    background-color: $gray;
  }
  &--gray {
    background-color: $white;
  }

  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(gap, 64);
  }
  &__features {
    display: flex;
    flex-direction: column;
    gap: 56px;
    padding: 32px 16px 0;
    position: relative;
    z-index: 1;
    @include media-breakpoint-up(sm) {
      gap: 80px;
      padding: 40px 40px 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
      @include responsive-spacing(padding-top, 40);
      @include responsive-spacing(padding-right, 40);
      @include responsive-spacing(padding-left, 40);
    }
    &-header {
      display: flex;
      flex-direction: column;
      gap: 16px;
      @include media-breakpoint-up(sm) {
        display: grid;
        grid-template-columns: auto 112px;
        justify-content: space-between;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
        grid-template-columns: auto px-to-vw(112);
      }
    }
    &-image {
      height: auto;
      margin: 0 0 0 auto;
      width: 85px;
      order: 1;
      @include media-breakpoint-up(sm) {
        order: 2;
        width: 100%;
      }
    }
    &-title {
      margin: 0;
      order: 2;
      @include media-breakpoint-up(sm) {
        order: 1;
      }
    }
    &-items {
      display: flex;
      flex-direction: column;
      gap: 32px;
      @include media-breakpoint-up(sm) {
        gap: 48px;
      }
      @include media-breakpoint-up(md) {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        column-gap: 96px;
        row-gap: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 96);
        @include responsive-spacing(row-gap, 48);
      }
    }
  }
  &__feature {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__services {
    display: grid;
    grid-template-columns: 1fr;
    list-style: none;
    position: relative;
    z-index: 1;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
      gap: 0;
      position: relative;
      &::before {
        background-color: $old-outline-inverse;
        content: '';
        height: 100%;
        left: calc(50% - 0.5px);
        position: absolute;
        top: 0;
        width: 1px;
      }
    }
    @include media-breakpoint-up(xl) {
      &::before {
        left: calc(50% - px-to-vw(0.5));
        @include responsive-spacing(width, 1);
      }
    }
  }
  &__service {
    align-items: center;
    border-top: 1px solid $old-outline-inverse;
    display: grid;
    grid-template-columns: auto 48px;
    min-height: 96px;
    padding: 24px 16px;
    @include media-breakpoint-up(sm) {
      min-height: 128px;
      padding: 40px 24px;
    }
    @include media-breakpoint-up(md) {
      padding: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      grid-template-columns: auto px-to-vw(48);
      @include responsive-spacing(min-height, 128);
      @include responsive-spacing(padding, 40);
    }
  }
}
</style>
